import { useParams, Link, Navigate } from "react-router-dom";
import Seo from "@/components/Seo";
import { breadcrumbJsonLd } from "@/lib/seo";
import EditorBadge from "@/components/EditorBadge";
import AdPlaceholder from "@/components/AdPlaceholder";
import AuthorsCard from "@/components/AuthorsCard";
import { getPost, POSTS } from "@/data/posts";
import { ChevronLeft, Clock } from "lucide-react";
import MortgageAffordabilityPost from "@/pages/blog/posts/MortgageAffordabilityPost";
import OvertimeTipsPost from "@/pages/blog/posts/OvertimeTipsPost";
import SnowballAvalanchePost from "@/pages/blog/posts/SnowballAvalanchePost";
import NoIncomeTaxStatesPost from "@/pages/blog/posts/NoIncomeTaxStatesPost";
import FirstTimeBuyerLoanTypesPost from "@/pages/blog/posts/FirstTimeBuyerLoanTypesPost";
import HowToPayOffCreditCardDebtFastPost from "@/pages/blog/posts/how-to-pay-off-credit-card-debt-fast.jsx";
import WhatIsAGoodInterestRateOnACarLoanPost from "@/pages/blog/posts/what-is-a-good-interest-rate-on-a-car-loan.jsx";
import HelocVsHomeEquityLoanPost from "@/pages/blog/posts/heloc-vs-home-equity-loan.jsx";
import HowMuchShouldISaveForRetirementPost from "@/pages/blog/posts/how-much-should-i-save-for-retirement.jsx";
import FhaVsConventionalLoanPost from "@/pages/blog/posts/fha-vs-conventional-loan.jsx";
import HowToNegotiateSalaryPost from "@/pages/blog/posts/how-to-negotiate-salary.jsx";
import CompoundInterestExplainedPost from "@/pages/blog/posts/compound-interest-explained.jsx";
import RentOrBuyCalculatorGuidePost from "@/pages/blog/posts/rent-or-buy-calculator-guide.jsx";
import VaLoanBenefitsExplainedPost from "@/pages/blog/posts/va-loan-benefits-explained.jsx";
import WhatIsRoiPost from "@/pages/blog/posts/what-is-roi.jsx";
import HowToCalculateTipPost from "@/pages/blog/posts/how-to-calculate-tip.jsx";
import JumboLoanRequirements2026Post from "@/pages/blog/posts/jumbo-loan-requirements-2026.jsx";
import SavingsGoalHowLongPost from "@/pages/blog/posts/savings-goal-how-long.jsx";
import SimpleInterestVsCompoundPost from "@/pages/blog/posts/simple-interest-vs-compound.jsx";
import MortgageRefinanceWhenWorthItPost from "@/pages/blog/posts/mortgage-refinance-when-worth-it.jsx";
import IncomeTaxBrackets2026Post from "@/pages/blog/posts/income-tax-brackets-2026.jsx";
import HowToGetOutOfDebtPost from "@/pages/blog/posts/how-to-get-out-of-debt.jsx";
// Route each blog slug to its hand-crafted content component.
const BODIES = {
    "how-much-mortgage-can-i-afford": MortgageAffordabilityPost,
    "no-tax-on-overtime-tips-explained": OvertimeTipsPost,
    "debt-snowball-vs-avalanche": SnowballAvalanchePost,
    "no-income-tax-states-actually-save": NoIncomeTaxStatesPost,
    "first-time-buyer-fha-va-conventional": FirstTimeBuyerLoanTypesPost,    
    "how-to-pay-off-credit-card-debt-fast": HowToPayOffCreditCardDebtFastPost,
    "what-is-a-good-interest-rate-on-a-car-loan": WhatIsAGoodInterestRateOnACarLoanPost,
    "heloc-vs-home-equity-loan": HelocVsHomeEquityLoanPost,
    "how-much-should-i-save-for-retirement": HowMuchShouldISaveForRetirementPost,
    "fha-vs-conventional-loan": FhaVsConventionalLoanPost,
    "how-to-negotiate-salary": HowToNegotiateSalaryPost,
    "compound-interest-explained": CompoundInterestExplainedPost,
    "rent-or-buy-calculator-guide": RentOrBuyCalculatorGuidePost,
    "va-loan-benefits-explained": VaLoanBenefitsExplainedPost,
    "what-is-roi": WhatIsRoiPost,
    "how-to-calculate-tip": HowToCalculateTipPost,
    "jumbo-loan-requirements-2026": JumboLoanRequirements2026Post,
    "savings-goal-how-long": SavingsGoalHowLongPost,
    "simple-interest-vs-compound": SimpleInterestVsCompoundPost,
    "mortgage-refinance-when-worth-it": MortgageRefinanceWhenWorthItPost,
    "income-tax-brackets-2026": IncomeTaxBrackets2026Post,
    "how-to-get-out-of-debt": HowToGetOutOfDebtPost,
};

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPost(slug || "");
    const Body = BODIES[slug || ""];

    if (!post || !Body) return <Navigate to="/blog" replace />;

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.description,
            datePublished: post.publishedAt,
            dateModified: post.updatedAt,
            author: [
                {
                    "@type": "Person",
                    name: "Vince P.",
                    jobTitle: "Co-founder, FinCalc",
                    worksFor: { "@type": "Organization", name: "Hi Tex Lawn Pro LLC" },
                    address: { "@type": "PostalAddress", addressRegion: "TX", addressCountry: "US" },
                },
                {
                    "@type": "Person",
                    name: "Jean P.",
                    jobTitle: "Co-founder, FinCalc",
                    worksFor: { "@type": "Organization", name: "Hi Tex Lawn Pro LLC" },
                    address: { "@type": "PostalAddress", addressRegion: "TX", addressCountry: "US" },
                },
            ],
            publisher: {
                "@type": "Organization",
                name: "FinCalc",
                parentOrganization: { "@type": "Organization", name: "Hi Tex Lawn Pro LLC" },
            },
        },
        breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
        ]),
    ];

    const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

    return (
        <>
            <Seo
                title={`${post.title} | FinCalc`}
                description={post.description}
                canonical={`/blog/${post.slug}`}
                jsonLd={jsonLd}
                ogSlug={post.slug}
            />

            <div className="container-editorial pt-8 pb-2">
                <nav className="flex items-center gap-2 text-xs text-muted-foreground" aria-label="Breadcrumb">
                    <Link to="/" className="hover:text-primary">Home</Link>
                    <span>/</span>
                    <Link to="/blog" className="hover:text-primary">Blog</Link>
                    <span>/</span>
                    <span className="text-primary truncate max-w-[200px] sm:max-w-none">{post.title}</span>
                </nav>
            </div>

            <article className="container-editorial pt-4 pb-16">
                <div className="max-w-3xl">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-accent mb-3">{post.category}</div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05] tracking-tight text-balance">
                        {post.title}
                    </h1>
                    <p className="mt-4 text-lg text-foreground/70 leading-relaxed">{post.description}</p>
                    <div className="mt-5 flex flex-wrap items-center gap-4">
                        <EditorBadge />
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-mono tabular">
                            <Clock className="w-3 h-3" strokeWidth={1.5} aria-hidden="true" />
                            {post.readTime} min read
                        </span>
                    </div>
                </div>

                <div className="mt-10 mb-8 max-w-3xl">
                    <AdPlaceholder type="leaderboard" dataTestId="ad-blog-post-top" />
                </div>

                <div className="prose-editorial max-w-3xl">
                    <Body />
                </div>

                <div className="mt-12 max-w-3xl">
                    <AdPlaceholder type="native" dataTestId="ad-blog-post-bottom" />
                </div>

                <div className="mt-12 max-w-3xl">
                    <AuthorsCard />
                </div>

                {related.length > 0 && (
                    <div className="mt-16 pt-8 border-t border-border max-w-3xl">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-accent mb-4">Keep reading</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {related.map((p) => (
                                <Link
                                    key={p.slug}
                                    to={`/blog/${p.slug}`}
                                    data-testid={`related-post-${p.slug}`}
                                    className="group bg-card rounded-lg border border-border p-4 hover:border-primary/30 transition-colors"
                                >
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-accent/70 mb-1.5">{p.category}</div>
                                    <div className="font-serif text-lg text-primary leading-tight group-hover:text-secondary">{p.title}</div>
                                </Link>
                            ))}
                        </div>
                        <Link to="/blog" className="inline-flex items-center gap-1.5 mt-6 text-sm text-primary hover:text-secondary">
                            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                            All articles
                        </Link>
                    </div>
                )}
            </article>
        </>
    );
}
